import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, useTheme } from '@mui/material';
import { FEED_CARD_WIDTH } from "app/appConstants";
import { MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcFilterCountInfo } from 'components/filter';
import RequestErrorUtils from "components/MprcRequestErrorReport";
import { FeedDataDto, FeedItemDto, FeedListQueryOrderBy, FeedType, GetListPermissionsResponse, PublicationSelectorDtoPaginatedList, PublicationSelectorOrder, PublicationSelectorType } from 'generated/CoreApi_v1';
import { usePageTitle } from "hooks/usePageTitle";
import { useWindowSize } from 'hooks/useWindowSize';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { CardManager } from 'views/knowledge/components/card/CardManager';
import { FeedCustomEventProps, publish, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { ListPublicationSelectorDrawer, PublicationSelectorDrawerResult } from 'views/knowledge/publication/drawers/ListPublicationSelectorDrawer';
import { isPublicationNotFound, PublicationNotFound } from "views/knowledge/publication/pages/common/PublicationNotFound";
import { FeedListFilter, IFeedListFilterOptions } from 'views/knowledge/publication/pages/List/FeedListFilter';
import { RightPanelContentPage } from "../common/RightPanelContentPage";
import { PublicationHeader } from '../components/PublicationHeader';
import { FeedListBox } from "./FeedListBox";

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    feedListMode?: boolean;
    isHome?: boolean;
}

export const ListViewer: React.FC<IProps> = ({ feedListMode = false, isHome = false }) => {

    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const api = useCoreApi();
    const theme = useTheme();

    const { width } = useWindowSize({ wait: 50 });
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [publication, setPublication] = useState<FeedItemDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [addItemList, setAddItemList] = useState<PublicationSelectorDrawerResult | null>(null);
    const [dialog, setDialog] = useState<any>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [count, setCount] = useState<number | undefined | null>();
    const permissions = useRef<GetListPermissionsResponse | null>(null);
    const [filterOptions, setFilterOptions] = useState<IFeedListFilterOptions>(location.state?.filter ?? {
        // Pelo acesso rápido, a ordem deve ser inicializada como "Mais Recente".
        orderBy: isHome ? FeedListQueryOrderBy.MostRecent : FeedListQueryOrderBy.DisplayOrder,
        searchText: ""
    });

    usePageTitle(t("Feed.List.PageTitle"));

    const breakpoint = feedListMode ? "lg" : "md";

    const publicationId = useRef<string | null>(null);

    ////////////////////////////////////////////////////////////////////////////////////////////////

    const filtered = filterOptions.searchText.length > 0;
    const isPublic = publication?.listDefinition?.isPublic ?? false;

    ///
    const handleFilterChange = useCallback((filter: IFeedListFilterOptions) => {
        setFilterOptions(filter);
        setCount(null);
    }, []);

    ///
    const handleRefreshData = (data?: FeedDataDto, resetFilter?: boolean) => {
        if (resetFilter) {
            setFilterOptions({
                orderBy: FeedListQueryOrderBy.DisplayOrder,
                searchText: ""
            });
            setCount(null);
        }
        setCount(data?.totalRows ?? 0);
    };

    ///
    const handleAdd = () => {
        setDialog(
            <ListPublicationSelectorDrawer
                title={t("Feed.Card.List.PublicationDrawerTitle")}
                createListExclusiveId={
                    isPublic && permissions.current?.canCreateExclusive == true
                        ? publicationId.current!
                        : undefined}
                onSelect={(publication) => setAddItemList(publication)}
                onClose={() => setDialog(null)}
                onAddNewPost={() => {
                    publish("ListItemAdded", publicationId.current!);
                    setDialog(null);
                }}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<PublicationSelectorDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeSelectors.publications({
                            searchText,
                            pageNumber,
                            pageSize,
                            selectorType: PublicationSelectorType.AddListItem,
                            listPublicationId: publicationId.current,
                            onlyPostPublications: false,
                            orderBy: PublicationSelectorOrder.Title,
                            userId: null
                        }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ///
    const load = () => {
        if (publicationId.current) {

            let markRead = true;

            // Se a solicitação foi realizada a partir do Feed de Histórico,
            // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
            if (location.state?.hasOwnProperty("previousPathname")) {
                const previousPathname = location.state.previousPathname as string;
                markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
            }

            setLoading(true);

            Promise.all([
                api.v1.knowledgeFeed.getFeedItem({ publicationId: publicationId.current, markRead }),
                api.v1.knowledgePublication.getListPermissions({ publicationId: publicationId.current })
            ])
                .then((results) => {
                    setPublication(results[0].data);
                    permissions.current = results[1].data;
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    ///
    const handlePublicationUpdate = (e: FeedCustomEventProps) => {
        if (e.detail == publicationId.current) {
            load();
        }
    };

    ///
    const handleAddItems = (e: FeedCustomEventProps) => {
        if (e.detail == publicationId.current) {
            handleAdd();
        }
    };

    ///
    const goBack = () => feedListMode
        ? navigate(KnowledgePaths.FeedStartup)
        : gobackOrHome();

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {

        subscribe("PublicationRestored", () => load());
        subscribe("PublicationPermanentlyDeleted", goBack);
        subscribe("ListAddItems", handleAddItems);
        subscribe("ListReload", handlePublicationUpdate);
        return () => {
            unsubscribe("ListAddItems", handleAddItems);
            unsubscribe("ListReload", handlePublicationUpdate);
            unsubscribe("PublicationRestored", () => load());
            unsubscribe("PublicationPermanentlyDeleted", goBack);
        };
    }, []);

    ///
    useEffect(() => {
        if (params.id) publicationId.current = params.id;

        if (publicationId.current != null) {
            // Carrega lista.
            load();
        }

    }, [document.location.pathname]);

    ///
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.knowledgePublication.addListItem({
                    listPublicationId: publicationId.current!,
                    itemPublicationId: addItemList!.publicationId
                });

                if (result) {
                    publish("ListItemAdded", publicationId.current!);
                }
            }
            catch (e) {
                const errorMessage = RequestErrorUtils.findValidationMessage(e, "itempublicationId.current", "ListPublicationItemAlreadyExists");

                if (errorMessage) enqueueSnackbar(errorMessage, { variant: "error" });
                else setViewError(e);
            }
            finally {
                setAddItemList(null);
            }
        }

        if (addItemList) postData();

    }, [addItemList]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderCardHeader = (
        publication &&
        <Box sx={{ width: FEED_CARD_WIDTH, margin: "0 auto" }}>
            <MprcContainer>
                <CardManager
                    feedType={FeedType.ContentPage}
                    pub={publication}
                    onNeedResetRowHeight={() => { }}
                    onReload={() => load()}
                    onFeedItemChanged={(data) => { if (data.deletedOn) goBack(); else load(); }}
                />
                <FeedListFilter
                    filterOptions={filterOptions}
                    listType={publication.listDefinition?.listType}
                    onAdd={
                        permissions.current?.canAddItems
                            ? handleAdd
                            : undefined
                    }
                    onFilterChange={handleFilterChange}
                />
            </MprcContainer>
            {
                count != undefined &&
                <MprcContainer>
                    <MprcFilterCountInfo
                        variant={filtered ? "bodyNotice" : (count ?? 0) == 0 ? "captionHeader" : "caption"}
                        text={t("Feed.List.FilterInfoMessage", { count: count ?? 0 })}
                        onResetFilter={filtered ? () => handleFilterChange({ ...filterOptions, searchText: "" }) : undefined}
                    />
                </MprcContainer>
            }
        </Box>
    );

    ///
    return (
        <>
            {dialog}

            {
                isPublicationNotFound(viewError)
                    ? <PublicationNotFound />
                    : viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : loading
                            ? <MprcInfiniteLoading visible />
                            : (
                                <>
                                    {!feedListMode &&
                                        <MprcContainer position="sticky" margin="none">
                                            <PublicationHeader
                                                onClose={goBack}
                                                actions={
                                                    width < theme.breakpoints.values[breakpoint]
                                                        ? [<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]
                                                        : []
                                                }
                                            />
                                        </MprcContainer>
                                    }

                                    <MprcRequestIndicator visible={addItemList != null} />

                                    <FeedListBox
                                        rightPanelBreakpoint={breakpoint}
                                        publication={publication!}
                                        feedType={FeedType.ListViewer}
                                        cardHeader={RenderCardHeader}
                                        rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                                        filter={filterOptions}
                                        count={count}
                                        permissions={permissions.current}
                                        onRefreshData={handleRefreshData}
                                        openRightPanel={openRightPanel}
                                        onCloseRightPanel={() => setOpenRightPanel(false)}
                                        onGobackStart={() => setCount(null)}
                                    />
                                </>
                            )
            }
        </>
    );
};
